/**
 * SideNavMenu.tsx — utvidbar meny i side-nav
 *
 * Ren React-komponent som erstatter cds-side-nav-menu.
 * Klikk toggler visning av underlenker, som glir åpen og igjen.
 * I rail-modus (menyen lukket, bare ikoner) åpner et klikk hele menyen og dropdownen.
 *
 * Props:
 * - title: meny-tittel
 * - icon: ikon foran tittelen
 * - current: menyen inneholder siden som er åpen
 * - children: SideNavMenuItem-komponenter
 */

import { useContext, useId, useState } from 'react';
import { ChevronDown } from '@carbon/icons-react';
import type { NavIconName } from '@/lib/navigation';
import { NAV_ICONS } from './nav-icons';
import { SideNavContext } from './SideNavContext';
import styles from './side-nav-menu.module.scss';

interface SideNavMenuProps {
  title: string;
  current?: boolean;
  icon?: NavIconName;
  children: React.ReactNode;
}

export default function SideNavMenu({ title, current = false, icon, children }: SideNavMenuProps) {
  const [expanded, setExpanded] = useState(false);
  const Icon = icon ? NAV_ICONS[icon] : null;
  const panelId = useId();
  const nav = useContext(SideNavContext);

  const toggle = () => {
    if (!nav.expanded) {
      // Rail: undermenyen får ikke plass, så åpne menyen og la dropdownen stå åpen
      nav.expand?.();
      setExpanded(true);
      return;
    }
    setExpanded(!expanded);
  };

  return (
    <li className={styles.menu}>
      <button
        type="button"
        className={styles.toggle}
        aria-expanded={expanded}
        aria-controls={panelId}
        data-current={current || undefined}
        title={nav.expanded ? undefined : title}
        onClick={toggle}
      >
        {Icon && <Icon size={20} className={styles.icon} aria-hidden="true" />}
        <span className="side-nav-text">{title}</span>
        {/* side-nav-chevron: global klasse — skjules i kollapset rail-modus */}
        <span className={`side-nav-chevron ${expanded ? styles.chevronExpanded : styles.chevron}`}>
          <ChevronDown size={16} aria-hidden="true" />
        </span>
      </button>
      {/* Alltid i DOM så høyden kan animeres; lukket er den skjult og kan ikke fokuseres.
          side-nav-sub-items: global klasse — skjules i kollapset rail-modus */}
      <div id={panelId} className={`side-nav-sub-items ${styles.collapse}`} data-open={expanded || undefined}>
        <ul className={styles.subItems} inert={!expanded}>
          {children}
        </ul>
      </div>
    </li>
  );
}
